import { isStaging } from "app/constants/env";
import { useClaimrWidget, useTheme } from "app/shared";
import { FC, useEffect, useState } from "react";
import { useDisconnect } from "wagmi";

export interface IClaimrParams {
  userToken?: {
    token: string;
  };
  questId?: string;
  onLoaded?: () => void;
}

function observeClaimr(nodeId: string, callback: any) {
  const observer = new MutationObserver((mutations) => {
    if (window.claimr) {
      observer.disconnect();
      callback(window.claimr);
    }
  });

  observer.observe(document.getElementById(nodeId) as Node, {
    attributes: true,
    childList: true,
  });
}

const QuestClaimrWidget: FC<IClaimrParams> = ({
  userToken,
  questId,
  onLoaded,
}) => {
  const { isInitialized, setInitialized } = useClaimrWidget();
  const { theme } = useTheme();
  const { disconnect } = useDisconnect();
  const containerId = "CLAIMR_CONTAINER_ID";
  const [retryCount, setRetryCount] = useState<number>(0);

  const handleLogout = () => {
    disconnect();
  };

  useEffect(() => {
    if (!userToken) {
      return;
    }

    const loadScript = () => {
      const script = document.createElement("script");
      script.src = `https://widgets.claimr.io/claimr.min.js?rnd=${Math.random()
        .toString(36)
        .substring(2)}`;
      script.id = "claimr-script";
      script.dataset.addons =
        "sup,slb,sur,sct,wcc,lam,esc,fcm,sqp,eap,eup,pvm,slc,scd,rcc,scc,dcc";
      script.dataset.organization = "inmind";
      script.dataset.campaign = isStaging()
        ? "alphamind_staging"
        : "alphamind-onboarding";

      script.dataset.autoresize = "true";
      script.dataset.container = containerId;
      script.async = true;
      script.setAttribute("data-theme-key", "alphamind-theme");
      //@ts-ignore
      script.setAttribute("data-user-token", userToken.token.token);

      script.onload = () => {
        observeClaimr(containerId, (claimr: any) => {
          claimr.set_theme(theme);
          questId && claimr.open_contest(questId);
          onLoaded?.();
          claimr.on_logout = handleLogout;
        });
        setInitialized(true);
      };

      script.onerror = () => {
        setRetryCount((prevCount) => prevCount + 1);
      };

      document.body.appendChild(script);
    };

    if (!isInitialized) {
      loadScript();
    } else {
      if (window.claimr) {
        //@ts-ignore
        window.claimr.set_theme(theme);
        //@ts-ignore
        window.claimr.on_logout = handleLogout;
      }
    }
  }, [userToken, isInitialized, setInitialized, theme, retryCount, questId]);

  useEffect(() => {
    if (retryCount > 0 && retryCount < 3) {
      const retryTimer = setTimeout(() => {
        const scriptElement = document.getElementById("claimr-script");
        if (scriptElement) {
          scriptElement.remove();
        }
        setInitialized(false);
      }, 3000);

      return () => clearTimeout(retryTimer);
    }
  }, [retryCount, setInitialized]);

  useEffect(() => {
    localStorage.setItem("alphamind-theme", theme);
    if (isInitialized && window.claimr) {
      //@ts-ignore
      window.claimr.set_theme(theme);
    }
  }, [theme, isInitialized]);

  return (
    <div
      id={containerId}
      style={{ width: "100%" }}
      data-theme-key="alphamind-theme"
    ></div>
  );
};

export default QuestClaimrWidget;
